<template>
  <div
    ref="root"
    :style="{
      position: 'relative',
      width: bbox.width + 'px',
      height: bbox.height + 'px',
      pointerEvents: 'none',
    }"
  >
    <svg
      ref="svg"
      :viewBox="viewBoxStr"
      :style="{
        position: 'absolute',
        left: 0,
        top: 0,
        width: bbox.width + 'px',
        height: bbox.height + 'px',
        overflow: 'visible',
        pointerEvents: 'none',
      }"
    >
      <!-- hit-lines invisiveis por segmento, dentro do wrapper .componente:
           alvo de clique-para-selecionar (via elementsFromPoint no canvas), sem
           handlers de movimento. Ficam presentes mesmo quando selecionado — as
           hit-lines do portal (na .view-canvas) ficam por cima e cuidam do
           arrasto/insercao, enquanto estas continuam permitindo (shift-)clique
           para (des)marcar. -->
      <template v-if="editable">
        <line
          v-for="(seg, i) in segmentsXY"
          :key="`hit-${i}`"
          :x1="seg.s.x"
          :y1="seg.s.y"
          :x2="seg.e.x"
          :y2="seg.e.y"
          stroke="transparent"
          :stroke-width="hitWidth"
          stroke-linecap="round"
          style="cursor: default; pointer-events: stroke"
        />
      </template>

      <!-- ===== modo flat-color ===== -->
      <template v-if="flat">
        <polyline
          :points="pointsAttr"
          fill="none"
          :stroke="pipeDark"
          :stroke-width="thickness"
          stroke-linejoin="round"
          stroke-linecap="butt"
          style="pointer-events: none"
        />
        <polyline
          v-if="flatInnerWidth > 0"
          :points="pointsAttr"
          fill="none"
          :stroke="color"
          :stroke-width="flatInnerWidth"
          stroke-linejoin="round"
          stroke-linecap="butt"
          style="pointer-events: none"
        />
      </template>

      <!-- ===== modo cilindrico 3D ===== -->
      <template v-else>
        <defs>
          <linearGradient
            v-for="(seg, i) in segmentShapes"
            :id="`${gradientId}-${i}`"
            :key="`grad-${i}`"
            gradientUnits="userSpaceOnUse"
            :x1="seg.start.x + seg.px * halfT"
            :y1="seg.start.y + seg.py * halfT"
            :x2="seg.start.x - seg.px * halfT"
            :y2="seg.start.y - seg.py * halfT"
          >
            <stop offset="0%" :stop-color="pipeDark" />
            <stop offset="8%" :stop-color="pipeMid" />
            <stop offset="22%" :stop-color="color" />
            <stop offset="38%" :stop-color="pipeLight" />
            <stop offset="50%" :stop-color="pipeHighlight" />
            <stop offset="62%" :stop-color="pipeLight" />
            <stop offset="78%" :stop-color="color" />
            <stop offset="92%" :stop-color="pipeMid" />
            <stop offset="100%" :stop-color="pipeDark" />
          </linearGradient>
          <mask :id="maskId">
            <polyline
              :points="pointsAttr"
              fill="none"
              stroke="white"
              :stroke-width="thickness"
              stroke-linejoin="round"
              stroke-linecap="butt"
            />
          </mask>
        </defs>

        <!-- borda externa escura -->
        <polyline
          :points="pointsAttr"
          fill="none"
          :stroke="pipeDark"
          :stroke-width="thickness + rimWidth * 2"
          stroke-linejoin="round"
          stroke-linecap="butt"
          style="pointer-events: none"
        />

        <!-- corpo cilindrico: poligonos miter mascarados pela forma round-join -->
        <g :mask="`url(#${maskId})`" style="pointer-events: none">
          <path
            v-for="(seg, i) in segmentShapes"
            :key="`body-${i}`"
            :d="seg.d"
            :fill="`url(#${gradientId}-${i})`"
          />
        </g>
      </template>

      <!-- setas direcionais no meio de cada segmento -->
      <path
        v-for="arrow in arrows"
        :key="arrow.key"
        :d="arrow.d"
        :fill="pipeDark"
        :fill-rule="arrow.fillRule"
        :transform="arrow.transform"
        style="pointer-events: none"
      />
    </svg>

    <!-- ===== camada editavel (so quando selecionado) =====
         Portada para o .view-canvas com z-index alto para ficar acima de
         QUALQUER componente do dashboard, mesmo com o cano visualmente atras —
         mas o CORPO do cano (svg acima) fica inline no wrapper, na sua "Ordem".
         O portal usa o MESMO viewBox/bbox e e posicionado pelo mesmo transform
         do wrapper (overlayTransform, reativo), entao as alcas alinham
         perfeitamente com o cano sem leitura de layout. -->
    <PipeOverlayPortal
      v-if="editable && selected"
      :view-box="viewBoxStr"
      :width="bbox.width"
      :height="bbox.height"
      :transform="overlayTransform"
      :target="canvasEl"
    >
      <!-- hit-lines por segmento: arrasta o corpo / double-click insere vertice -->
      <line
        v-for="(seg, i) in segmentsXY"
        :key="`edit-hit-${i}`"
        :x1="seg.s.x"
        :y1="seg.s.y"
        :x2="seg.e.x"
        :y2="seg.e.y"
        stroke="transparent"
        :stroke-width="hitWidth"
        stroke-linecap="butt"
        style="cursor: move; pointer-events: stroke"
        @pointerdown="startBodyDrag"
        @dblclick="(ev) => onSegmentDoubleClick(i, ev)"
      />

      <!-- guias de alinhamento (snap) -->
      <line
        v-for="(guide, i) in activeGuides"
        :key="`guide-${i}`"
        :x1="guide.axis === 'x' ? guide.value : -9999"
        :y1="guide.axis === 'y' ? guide.value : -9999"
        :x2="guide.axis === 'x' ? guide.value : 9999"
        :y2="guide.axis === 'y' ? guide.value : 9999"
        stroke="#2563eb"
        stroke-width="1"
        stroke-dasharray="4 3"
        stroke-linecap="round"
      />

      <!-- handles de vertice -->
      <circle
        v-for="(p, i) in localPoints"
        :key="`handle-${i}`"
        :cx="p.x"
        :cy="p.y"
        :r="handleRadius"
        fill="#ffffff"
        stroke="#2563eb"
        :stroke-width="2"
        style="cursor: move; pointer-events: all; touch-action: none"
        @pointerdown="(ev) => startVertexDrag(i, ev)"
        @contextmenu="(ev) => onVertexContextMenu(i, ev)"
      />
    </PipeOverlayPortal>
  </div>
</template>

<script>
import { PipeArrowDirection } from '@/utils/componente'
import pipeSnapRegistry from '@/utils/pipe-snap-registry'

import PipeOverlayPortal from './PipeOverlayPortal.vue'

const MIN_HIT_WIDTH = 20
const BBOX_PADDING = 12
const HANDLE_RADIUS = 6
const SNAP_THRESHOLD = 6
const ARROW_MIN_SIZE = 8

let pipeUidCounter = 0

function clampByte(v) {
  return Math.max(0, Math.min(255, Math.round(v)))
}

function shiftHexColor(hex, amount) {
  const clean = (hex || '').trim()
  const short = /^#([0-9a-fA-F]{3})$/
  const full = /^#([0-9a-fA-F]{6})$/
  const toHex = (n) => clampByte(n).toString(16).padStart(2, '0')

  if (short.test(clean)) {
    const m = clean.match(short)
    const [r, g, b] = m[1].split('').map((ch) => parseInt(ch + ch, 16))
    return `#${toHex(r + amount)}${toHex(g + amount)}${toHex(b + amount)}`
  }
  if (full.test(clean)) {
    const raw = clean.match(full)[1]
    const r = parseInt(raw.slice(0, 2), 16)
    const g = parseInt(raw.slice(2, 4), 16)
    const b = parseInt(raw.slice(4, 6), 16)
    return `#${toHex(r + amount)}${toHex(g + amount)}${toHex(b + amount)}`
  }
  return hex
}

function computeMiterOffset(dIn, dOut, halfT) {
  const nlIn = { x: -dIn.y, y: dIn.x }
  const nlOut = { x: -dOut.y, y: dOut.x }
  const crossD = dIn.x * dOut.y - dIn.y * dOut.x
  if (Math.abs(crossD) < 0.01) return null
  const dnl = { x: nlOut.x - nlIn.x, y: nlOut.y - nlIn.y }
  const t = (halfT * (dnl.x * dOut.y - dnl.y * dOut.x)) / crossD
  const off = {
    x: nlIn.x * halfT + t * dIn.x,
    y: nlIn.y * halfT + t * dIn.y,
  }
  if (Math.hypot(off.x, off.y) > halfT * 4) return null
  return off
}

// Clona o objeto de valor do pipe (apenas free points sao suportados na UI).
function clonePipeValue(v) {
  return {
    ...v,
    points: (v.points || []).map((p) => ({
      free: p.free ? { x: p.free.x, y: p.free.y } : undefined,
      anchored: p.anchored ? { ...p.anchored } : undefined,
    })),
  }
}

function screenToSvgLocal(svg, clientX, clientY) {
  const ctm = svg.getScreenCTM()
  if (!ctm) return { x: clientX, y: clientY }
  const pt = svg.createSVGPoint()
  pt.x = clientX
  pt.y = clientY
  const local = pt.matrixTransform(ctm.inverse())
  return { x: local.x, y: local.y }
}

export default {
  name: 'ViewShowPipe',
  components: {
    PipeOverlayPortal,
  },
  props: {
    componente: {
      type: Object,
      required: true,
    },
    editable: {
      type: Boolean,
      default: false,
    },
    // Quando true, o cano esta selecionado e libera a edicao (arrasto de corpo
    // e de vertice). Sem selecao, as hit-lines servem apenas para clicar e
    // selecionar — nada se move.
    selected: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      // override local mantem o arrasto fluido antes do pai persistir e o prop
      // voltar como fonte da verdade.
      override: null,
      activeGuides: [],
      uid: `pipe-${pipeUidCounter++}`,
      // Offset de arrasto do corpo ao vivo: durante o body-drag o wrapper e
      // movido direto no DOM (sem passar por componente.x/y ainda), entao o
      // portal (que espelha o transform do wrapper) precisa deste delta para
      // acompanhar. Zerado no fim do drag, quando componente.x/y ja incorporou.
      dragDx: 0,
      dragDy: 0,
      // Elemento .view-canvas onde o portal editavel e ancorado. Resolvido uma
      // vez no mounted (este componente persiste entre selecoes, entao a
      // referencia serve para todas as re-montagens do portal) para o portal
      // nao depender do proprio $el estar posicionado no DOM ao montar.
      canvasEl: null,
    }
  },
  computed: {
    value() {
      return this.override || this.componente.value || {}
    },
    points() {
      return this.value.points || []
    },
    localPoints() {
      return this.points.map((p) =>
        p.free ? { x: p.free.x, y: p.free.y } : { x: 0, y: 0 }
      )
    },
    color() {
      return this.value.color || '#6b7280'
    },
    pipeDark() {
      return shiftHexColor(this.color, -45)
    },
    pipeMid() {
      return shiftHexColor(this.color, -15)
    },
    pipeLight() {
      return shiftHexColor(this.color, 50)
    },
    pipeHighlight() {
      return shiftHexColor(this.color, 80)
    },
    thickness() {
      return this.value.thickness > 0 ? this.value.thickness : 12
    },
    halfT() {
      return this.thickness / 2
    },
    rimWidth() {
      return Math.max(1, this.thickness * 0.06)
    },
    flat() {
      return !!this.value.flatColor
    },
    flatInnerWidth() {
      const pct = this.value.borderThicknessPercent || 0
      const borderWidth = (this.thickness * pct) / 100
      return this.thickness - borderWidth * 2
    },
    hitWidth() {
      return Math.max(this.thickness, MIN_HIT_WIDTH)
    },
    handleRadius() {
      return HANDLE_RADIUS
    },
    pad() {
      return this.thickness / 2 + BBOX_PADDING
    },
    bbox() {
      if (this.localPoints.length === 0) {
        return { width: 1, height: 1 }
      }
      const xs = this.localPoints.map((p) => p.x)
      const ys = this.localPoints.map((p) => p.y)
      return {
        width: Math.max(1, Math.max(...xs) + this.pad * 2),
        height: Math.max(1, Math.max(...ys) + this.pad * 2),
      }
    },
    viewBoxStr() {
      return `${-this.pad} ${-this.pad} ${this.bbox.width} ${this.bbox.height}`
    },
    // Transform do portal editavel: espelha o do wrapper .componente
    // (ViewComponentes.componenteWrapperStyle = translate(x,y) scale(sx,sy)),
    // somando o offset de arrasto ao vivo. Como o portal fica em left:0/top:0
    // dentro do .view-canvas, este transform o sobrepoe exatamente ao cano.
    overlayTransform() {
      const x = (this.componente.x || 0) + this.dragDx
      const y = (this.componente.y || 0) + this.dragDy
      const sx = this.componente.scaleX || 1
      const sy = this.componente.scaleY || 1
      return `translate(${x}px, ${y}px) scale(${sx}, ${sy})`
    },
    pointsAttr() {
      return this.localPoints.map((p) => `${p.x},${p.y}`).join(' ')
    },
    gradientId() {
      return `pipe-cylinder-${this.uid}`
    },
    maskId() {
      return `pipe-mask-${this.uid}`
    },
    segmentsXY() {
      const res = []
      for (let i = 0; i < this.localPoints.length - 1; i++) {
        res.push({ s: this.localPoints[i], e: this.localPoints[i + 1] })
      }
      return res
    },
    segDirs() {
      return this.segmentsXY.map((seg) => {
        const dx = seg.e.x - seg.s.x
        const dy = seg.e.y - seg.s.y
        const len = Math.hypot(dx, dy)
        return len > 0.5 ? { x: dx / len, y: dy / len } : { x: 1, y: 0 }
      })
    },
    segmentShapes() {
      const resolved = this.localPoints
      const numSegs = resolved.length - 1
      if (numSegs < 1) return []
      const halfT = this.halfT
      const segDirs = this.segDirs

      const miterAt = new Array(resolved.length).fill(null)
      for (let j = 1; j < resolved.length - 1; j++) {
        miterAt[j] = computeMiterOffset(segDirs[j - 1], segDirs[j], halfT)
      }

      return resolved.slice(0, -1).map((start, i) => {
        const end = resolved[i + 1]
        const dir = segDirs[i]
        const nl = { x: -dir.y * halfT, y: dir.x * halfT }

        const edgeAt = (v, miter) => {
          if (!miter) {
            return {
              a: { x: v.x + nl.x, y: v.y + nl.y },
              b: { x: v.x - nl.x, y: v.y - nl.y },
            }
          }
          const c1 = { x: v.x + miter.x, y: v.y + miter.y }
          const c2 = { x: v.x - miter.x, y: v.y - miter.y }
          const dot = (c1.x - v.x) * nl.x + (c1.y - v.y) * nl.y
          return dot >= 0 ? { a: c1, b: c2 } : { a: c2, b: c1 }
        }

        const sEdge = edgeAt(start, i > 0 ? miterAt[i] : null)
        const eEdge = edgeAt(end, i < numSegs - 1 ? miterAt[i + 1] : null)

        const d =
          `M ${sEdge.a.x} ${sEdge.a.y} L ${eEdge.a.x} ${eEdge.a.y}` +
          ` L ${eEdge.b.x} ${eEdge.b.y} L ${sEdge.b.x} ${sEdge.b.y} Z`

        return { d, px: -dir.y, py: dir.x, start }
      })
    },
    arrowHalfWidth() {
      if (this.flat) return Math.max(ARROW_MIN_SIZE / 2, this.thickness * 1.0)
      const gradientInner = this.thickness - this.rimWidth * 2
      return Math.max(ARROW_MIN_SIZE / 2, gradientInner * 0.35)
    },
    arrowLength() {
      return this.arrowHalfWidth * 2
    },
    arrows() {
      const arrow = this.value.arrow
      if (
        arrow === undefined ||
        arrow === PipeArrowDirection.None ||
        arrow === PipeArrowDirection.Unspecified
      ) {
        return []
      }
      const isFlat = this.flat
      const arrowLength = this.arrowLength
      const hw = this.arrowHalfWidth
      const result = []

      const buildSingle = () => {
        const headLen = arrowLength * 0.55
        const tipX = arrowLength / 2
        if (isFlat) {
          return `M ${tipX} 0 L ${tipX - headLen} ${-hw} L ${
            tipX - headLen
          } ${hw} Z`
        }
        const shaftHW = hw * 0.35
        return (
          `M ${tipX} 0` +
          ` L ${tipX - headLen} ${-hw}` +
          ` L ${tipX - headLen} ${-shaftHW}` +
          ` L ${-tipX} ${-shaftHW}` +
          ` L ${-tipX} ${shaftHW}` +
          ` L ${tipX - headLen} ${shaftHW}` +
          ` L ${tipX - headLen} ${hw}` +
          ` Z`
        )
      }

      this.segmentsXY.forEach((seg, i) => {
        const dx = seg.e.x - seg.s.x
        const dy = seg.e.y - seg.s.y
        const len = Math.hypot(dx, dy)
        if (len < 1) return
        const mx = (seg.s.x + seg.e.x) / 2
        const my = (seg.s.y + seg.e.y) / 2
        const angle = (Math.atan2(dy, dx) * 180) / Math.PI

        if (arrow === PipeArrowDirection.Forward) {
          result.push({
            key: `arrow-fwd-${i}`,
            d: buildSingle(),
            fillRule: 'nonzero',
            transform: `translate(${mx} ${my}) rotate(${angle})`,
          })
        } else if (arrow === PipeArrowDirection.Backward) {
          result.push({
            key: `arrow-back-${i}`,
            d: buildSingle(),
            fillRule: 'nonzero',
            transform: `translate(${mx} ${my}) rotate(${angle + 180})`,
          })
        } else if (arrow === PipeArrowDirection.Both) {
          const bothLen = isFlat ? arrowLength : arrowLength * 1.6
          const tipX = bothLen / 2
          const headLen = bothLen * 0.35
          const shaftHW = hw * 0.35
          const bothD = isFlat
            ? `M ${tipX} 0 L ${tipX - headLen} ${-hw} L ${
                tipX - headLen
              } ${hw} Z` +
              ` M ${-tipX} 0 L ${-tipX + headLen} ${-hw} L ${
                -tipX + headLen
              } ${hw} Z`
            : `M ${tipX} 0 L ${tipX - headLen} ${-hw} L ${
                tipX - headLen
              } ${-shaftHW}` +
              ` L ${-tipX + headLen} ${-shaftHW} L ${
                -tipX + headLen
              } ${-hw} L ${-tipX} 0` +
              ` L ${-tipX + headLen} ${hw} L ${-tipX + headLen} ${shaftHW}` +
              ` L ${tipX - headLen} ${shaftHW} L ${tipX - headLen} ${hw} Z`
          result.push({
            key: `arrow-both-${i}`,
            d: bothD,
            fillRule: 'evenodd',
            transform: `translate(${mx} ${my}) rotate(${angle})`,
          })
        }
      })
      return result
    },
  },
  watch: {
    'componente.value'() {
      // Pai persistiu/recarregou: limpa override, prop volta a ser a verdade.
      this.override = null
      this.$nextTick(() => this.updateSnapRegistry())
    },
    localPoints() {
      this.$nextTick(() => this.updateSnapRegistry())
    },
    // O cano pode ser movido/escalado por FORA (ex.: arrasto de grupo do
    // moveable numa selecao multipla), mutando so componente.x/y/scale — sem
    // mexer em value/localPoints. Reregistra os pontos (coords de pagina) para o
    // snap entre canos acompanhar a nova posicao. Os handles editaveis sao inline
    // no wrapper, entao ja acompanham o transform sozinhos.
    'componente.x'() {
      this.$nextTick(() => this.updateSnapRegistry())
    },
    'componente.y'() {
      this.$nextTick(() => this.updateSnapRegistry())
    },
    'componente.scaleX'() {
      this.$nextTick(() => this.updateSnapRegistry())
    },
    'componente.scaleY'() {
      this.$nextTick(() => this.updateSnapRegistry())
    },
    selected() {
      this.$nextTick(() => this.updateSnapRegistry())
    },
  },
  mounted() {
    // Resolve o .view-canvas uma vez (referencia estavel) para ancorar o portal
    // editavel em todas as re-montagens (a cada selecao) sem depender do $el do
    // portal estar no DOM ao montar.
    this.canvasEl = this.$refs.root
      ? this.$refs.root.closest('.view-canvas')
      : null
    this.$nextTick(() => this.updateSnapRegistry())
    window.addEventListener('scroll', this.updateSnapRegistry, {
      passive: true,
    })
    window.addEventListener('resize', this.updateSnapRegistry)
  },
  beforeDestroy() {
    pipeSnapRegistry.unregister(this.uid)
    window.removeEventListener('scroll', this.updateSnapRegistry)
    window.removeEventListener('resize', this.updateSnapRegistry)
  },
  methods: {
    // Posiciona os pontos resolvidos em coords de pagina para o snap entre canos.
    updateSnapRegistry() {
      const svg = this.$refs.svg
      if (!svg) return
      const rect = svg.getBoundingClientRect()
      const ox = rect.left + window.scrollX + this.pad
      const oy = rect.top + window.scrollY + this.pad
      pipeSnapRegistry.register(
        this.uid,
        this.localPoints.map((p) => ({ x: p.x + ox, y: p.y + oy }))
      )
    },
    emitChange(nextValue) {
      this.override = nextValue
      this.$emit('change', nextValue)
    },
    withFreePointAt(value, idx, x, y) {
      const next = clonePipeValue(value)
      if (next.points[idx] && next.points[idx].free) {
        next.points[idx] = { free: { x, y } }
      }
      return next
    },
    withPointInsertedAt(value, segmentIdx, x, y) {
      const next = clonePipeValue(value)
      next.points.splice(segmentIdx + 1, 0, { free: { x, y } })
      return next
    },
    withPointRemovedAt(value, idx) {
      const next = clonePipeValue(value)
      next.points.splice(idx, 1)
      return next
    },
    startVertexDrag(idx, e) {
      if (e.button !== 0) return
      if (!this.editable) return
      const point = this.points[idx]
      if (!point || !point.free) return

      e.stopPropagation()
      e.preventDefault()
      if (e.target.setPointerCapture) e.target.setPointerCapture(e.pointerId)

      const svg = this.$refs.svg
      if (!svg) return
      const ctm = svg.getScreenCTM()
      if (!ctm) return
      const ctmInv = ctm.inverse()
      const startPt = new DOMPoint(e.clientX, e.clientY).matrixTransform(ctmInv)
      const startX = point.free.x
      const startY = point.free.y
      const base = this.value

      let latest = base

      const onMove = (ev) => {
        const cur = new DOMPoint(ev.clientX, ev.clientY).matrixTransform(ctmInv)
        const dx = cur.x - startPt.x
        const dy = cur.y - startPt.y

        const svgRect = svg.getBoundingClientRect()
        const pageOx = svgRect.left + window.scrollX + this.pad
        const pageOy = svgRect.top + window.scrollY + this.pad

        let pageX = startX + dx + pageOx
        let pageY = startY + dy + pageOy

        const ownOthers = this.localPoints
          .filter((_, j) => j !== idx)
          .map((p) => ({ x: p.x + pageOx, y: p.y + pageOy }))
        const crossPipe = pipeSnapRegistry.getOtherPoints(this.uid)
        const candidates = [...ownOthers, ...crossPipe]

        let bestX = null
        let bestY = null
        for (const other of candidates) {
          const ddx = Math.abs(pageX - other.x)
          if (ddx <= SNAP_THRESHOLD && (!bestX || ddx < bestX.dist)) {
            bestX = { dist: ddx, value: other.x }
          }
          const ddy = Math.abs(pageY - other.y)
          if (ddy <= SNAP_THRESHOLD && (!bestY || ddy < bestY.dist)) {
            bestY = { dist: ddy, value: other.y }
          }
        }
        const guides = []
        if (bestX) {
          pageX = bestX.value
          guides.push({ axis: 'x', value: bestX.value - pageOx })
        }
        if (bestY) {
          pageY = bestY.value
          guides.push({ axis: 'y', value: bestY.value - pageOy })
        }

        this.activeGuides = guides
        latest = this.withFreePointAt(base, idx, pageX - pageOx, pageY - pageOy)
        this.override = latest
      }
      const onUp = () => {
        window.removeEventListener('pointermove', onMove)
        window.removeEventListener('pointerup', onUp)
        window.removeEventListener('pointercancel', onUp)
        this.activeGuides = []
        this.emitChange(latest)
      }
      window.addEventListener('pointermove', onMove)
      window.addEventListener('pointerup', onUp)
      window.addEventListener('pointercancel', onUp)
    },
    onSegmentDoubleClick(segmentIdx, e) {
      if (!this.editable) return
      if (!this.selected) return
      e.stopPropagation()
      e.preventDefault()
      const svg = this.$refs.svg
      if (!svg) return
      const local = screenToSvgLocal(svg, e.clientX, e.clientY)
      const next = this.withPointInsertedAt(
        this.value,
        segmentIdx,
        local.x,
        local.y
      )
      this.emitChange(next)
    },
    onVertexContextMenu(idx, e) {
      if (!this.editable) return
      if (this.points.length <= 2) return
      e.preventDefault()
      e.stopPropagation()
      this.emitChange(this.withPointRemovedAt(this.value, idx))
    },
    startBodyDrag(e) {
      if (e.button !== 0) return
      if (!this.editable) return
      // So move depois de selecionado. Sem selecao, deixa o evento seguir para
      // o fluxo de selecao do canvas (o clique seleciona o cano).
      if (!this.selected) return
      e.stopPropagation()
      e.preventDefault()
      if (e.target.setPointerCapture) e.target.setPointerCapture(e.pointerId)

      const wrapper = this.$refs.root
        ? this.$refs.root.closest('.componente')
        : null

      const startX = e.clientX
      const startY = e.clientY
      const origTransform = wrapper ? wrapper.style.transform : ''
      const match = origTransform.match(
        /translate\(([-\d.]+)px,\s*([-\d.]+)px\)/
      )
      const origTx = match ? parseFloat(match[1]) : 0
      const origTy = match ? parseFloat(match[2]) : 0

      let dragging = false
      let lastDx = 0
      let lastDy = 0
      const THRESHOLD = 3

      const onMove = (ev) => {
        lastDx = ev.clientX - startX
        lastDy = ev.clientY - startY
        if (!dragging && Math.hypot(lastDx, lastDy) < THRESHOLD) return
        dragging = true
        if (wrapper) {
          wrapper.style.transform = `translate(${origTx + lastDx}px, ${
            origTy + lastDy
          }px)`
        }
        // O corpo (wrapper) foi movido direto no DOM sem passar por
        // componente.x/y: espelha o delta no portal via dragDx/dragDy para as
        // alcas acompanharem o cano ao vivo. Tambem reregistra os pontos (coords
        // de pagina) para o snap entre canos acompanhar o corpo em movimento.
        this.dragDx = lastDx
        this.dragDy = lastDy
        this.updateSnapRegistry()
      }
      const onUp = () => {
        window.removeEventListener('pointermove', onMove)
        window.removeEventListener('pointerup', onUp)
        window.removeEventListener('pointercancel', onUp)
        if (dragging) {
          // O pai (onPipeBodyMove) incorpora o delta em componente.x/y de forma
          // reativa; zera o offset para o portal nao somar o delta duas vezes.
          this.$emit('bodymove', { dx: lastDx, dy: lastDy })
          this.dragDx = 0
          this.dragDy = 0
        }
      }
      window.addEventListener('pointermove', onMove)
      window.addEventListener('pointerup', onUp)
      window.addEventListener('pointercancel', onUp)
    },
  },
}
</script>

<style scoped></style>
